import React, { useState } from 'react';
import { DocumentTextIcon, ArrowDownTrayIcon, BuildingOffice2Icon, CalendarIcon, TruckIcon, CubeIcon, DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { XmlCte } from '../../types/cte';
import { useBaixarXmlCte, useGerarDacteCte } from '../../hooks/useCte';
import { money, formatCnpj, formatTipoFrete, formatDateTime } from '../../utils/format';

interface CteListProps {
  xmlCtes: XmlCte[];
  onSelect: (cte: XmlCte) => void;
  isLoading?: boolean;
}

const CteList: React.FC<CteListProps> = ({ xmlCtes, onSelect, isLoading }) => {
  const baixarXmlMutation = useBaixarXmlCte();
  const gerarDacteMutation = useGerarDacteCte();
  const [copiadoId, setCopiadoId] = useState<number | null>(null);

  const handleBaixarXml = (e: React.MouseEvent, cteId: number) => { e.stopPropagation(); baixarXmlMutation.mutate(cteId); };
  const handleGerarDacte = (e: React.MouseEvent, cteId: number) => { e.stopPropagation(); gerarDacteMutation.mutate(cteId); };
  const handleCopiarChave = async (e: React.MouseEvent, cte: XmlCte) => {
    e.stopPropagation();
    if (!cte.chaveAcesso) return;
    try {
      await navigator.clipboard.writeText(cte.chaveAcesso);
      setCopiadoId(cte.id);
      setTimeout(() => setCopiadoId((cur) => (cur === cte.id ? null : cur)), 1500);
    } catch { /* silencioso */ }
  };

  // Skeleton estrutural (nunca tela em branco).
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
        <div className="divide-y divide-line">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="h-3 w-10 animate-pulse rounded-iris-sm bg-surface-2" />
              <div className="h-3 flex-1 animate-pulse rounded-iris-sm bg-surface-2" />
              <div className="hidden h-3 w-24 animate-pulse rounded-iris-sm bg-surface-2 md:block" />
              <div className="hidden h-3 w-20 animate-pulse rounded-iris-sm bg-surface-2 md:block" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!xmlCtes || xmlCtes.length === 0) {
    return (
      <div className="rounded-iris-lg border border-line bg-surface px-6 py-12 text-center">
        <DocumentTextIcon className="mx-auto h-10 w-10 text-tx-3" />
        <h3 className="mt-3 text-sm font-semibold text-tx">Nenhum CT-e encontrado</h3>
        <p className="mt-1 text-sm text-tx-3">Nenhum conhecimento de transporte no período/filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
      {/* Header da tabela densa — só de md pra cima (fronteira tabela↔card). */}
      <div className="hidden border-b border-line bg-surface-2 px-4 py-2 md:block">
        <div className="grid grid-cols-12 gap-2 text-[11px] font-medium uppercase tracking-wide text-tx-3">
          <div className="col-span-1">CT-e</div>
          <div className="col-span-3">Emitente</div>
          <div className="col-span-2">Remetente</div>
          <div className="col-span-2">Destinatário</div>
          <div className="col-span-2 text-right">Data / Valor</div>
          <div className="col-span-1 text-right">Frete</div>
          <div className="col-span-1 text-right">Ações</div>
        </div>
      </div>

      <div className="divide-y divide-line">
        {xmlCtes.map((cte) => (
          <div
            key={cte.id}
            onClick={() => onSelect(cte)}
            className="cursor-pointer transition-colors hover:bg-surface-2"
          >
            {/* ── Mobile / tablet estreito (< md): card ── */}
            <div className="p-3 md:hidden">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-tx">CT-e {cte.numero || 'S/N'}</span>
                    {cte.serie && <span className="text-[11px] text-tx-3">Série {cte.serie}</span>}
                  </div>
                  <p className="mt-0.5 text-[11px] text-tx-3">NSU {cte.nsu}</p>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums text-tx">{money(cte.valorTotal)}</div>
                  <div className="text-[11px] tabular-nums text-tx-3">{formatDateTime(cte.dataEmissao)}</div>
                </div>
              </div>

              <dl className="mt-2.5 space-y-1.5 text-[13px]">
                <div className="flex items-center gap-2 text-tx">
                  <BuildingOffice2Icon className="h-4 w-4 flex-shrink-0 text-tx-3" />
                  <span className="truncate font-medium">{cte.emitenteNome || 'Emitente não informado'}</span>
                </div>
                <div className="flex items-center gap-2 text-tx-2">
                  <TruckIcon className="h-4 w-4 flex-shrink-0 text-tx-3" />
                  <span className="truncate">{cte.remetenteNome || 'Remetente —'}</span>
                </div>
                <div className="flex items-center gap-2 text-tx-2">
                  <CubeIcon className="h-4 w-4 flex-shrink-0 text-tx-3" />
                  <span className="truncate">{cte.destinatarioNome || 'Destinatário —'}</span>
                </div>
              </dl>

              <div className="mt-2.5 flex items-center justify-between gap-2">
                <div className="text-[13px]">
                  <span className="text-tx-3">Frete </span>
                  <span className="font-medium tabular-nums text-tx">{money(cte.valorFrete)}</span>
                  {cte.tipoPagamento != null && (
                    <span className={`ml-2 font-medium ${formatTipoFrete(cte.tipoPagamento).color}`}>
                      {formatTipoFrete(cte.tipoPagamento).label}
                    </span>
                  )}
                </div>
                {cte.cnpjConsultante && (
                  <span className="truncate text-[11px] text-accent-text" title={`Baixado por ${formatCnpj(cte.cnpjConsultante)}`}>
                    {formatCnpj(cte.cnpjConsultante)}
                  </span>
                )}
              </div>

              {/* Ações — neutras (baixar/gerar não é sucesso nem perigo); alvo de toque ≥ 44px */}
              <div className="mt-3 flex gap-2 border-t border-line pt-3">
                {cte.chaveAcesso && (
                  <button
                    onClick={(e) => handleCopiarChave(e, cte)}
                    className="inline-flex min-h-[44px] w-11 flex-shrink-0 items-center justify-center rounded-iris border border-line bg-surface-2 text-tx-2 transition-colors hover:bg-surface"
                    title="Copiar chave de acesso"
                    aria-label="Copiar chave de acesso"
                  >
                    {copiadoId === cte.id ? <CheckIcon className="h-4 w-4 text-ok" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                  </button>
                )}
                <button
                  onClick={(e) => handleBaixarXml(e, cte.id)}
                  disabled={baixarXmlMutation.isPending}
                  className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-iris border border-line bg-surface-2 text-sm font-medium text-tx transition-colors hover:bg-surface disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-tx-2" /> Baixar XML
                </button>
                <button
                  onClick={(e) => handleGerarDacte(e, cte.id)}
                  disabled={gerarDacteMutation.isPending}
                  className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-iris border border-line bg-surface-2 text-sm font-medium text-tx transition-colors hover:bg-surface disabled:opacity-50"
                >
                  <DocumentTextIcon className="h-4 w-4 text-tx-2" /> Gerar DACTE
                </button>
              </div>
            </div>

            {/* ── Desktop (md+): linha densa ── */}
            <div className="hidden grid-cols-12 items-center gap-2 px-4 py-2 text-[13px] md:grid">
              <div className="col-span-1 min-w-0">
                <div className="font-semibold text-tx">{cte.numero || 'S/N'}</div>
                {cte.serie && <div className="text-[11px] text-tx-3">Sér. {cte.serie}</div>}
                {cte.cnpjConsultante && (
                  <div className="truncate text-[10px] text-accent-text" title={`Baixado por ${formatCnpj(cte.cnpjConsultante)}`}>
                    {formatCnpj(cte.cnpjConsultante)}
                  </div>
                )}
              </div>

              <div className="col-span-3 min-w-0">
                <div className="truncate font-medium text-tx">{cte.emitenteNome || 'Não informado'}</div>
                <div className="truncate text-[11px] tabular-nums text-tx-3">{formatCnpj(cte.emitenteCnpj)}</div>
              </div>

              <div className="col-span-2 min-w-0">
                <div className="truncate text-tx-2">{cte.remetenteNome || '—'}</div>
                <div className="truncate text-[11px] tabular-nums text-tx-3">{formatCnpj(cte.remetenteCnpj)}</div>
              </div>

              <div className="col-span-2 min-w-0">
                <div className="truncate text-tx-2">{cte.destinatarioNome || '—'}</div>
                <div className="truncate text-[11px] tabular-nums text-tx-3">{formatCnpj(cte.destinatarioCnpj)}</div>
              </div>

              <div className="col-span-2 min-w-0 text-right">
                <div className="font-medium tabular-nums text-tx">{money(cte.valorTotal)}</div>
                <div className="flex items-center justify-end gap-1 text-[11px] tabular-nums text-tx-3">
                  <CalendarIcon className="h-3 w-3" />{formatDateTime(cte.dataEmissao)}
                </div>
              </div>

              <div className="col-span-1 min-w-0 text-right">
                <div className="font-medium tabular-nums text-tx">{money(cte.valorFrete)}</div>
                <div className={`truncate text-[11px] font-medium ${formatTipoFrete(cte.tipoPagamento).color}`}>
                  {formatTipoFrete(cte.tipoPagamento).label}
                </div>
              </div>

              <div className="col-span-1 flex items-center justify-end gap-0.5">
                {cte.chaveAcesso && (
                  <button
                    onClick={(e) => handleCopiarChave(e, cte)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx"
                    title="Copiar chave de acesso"
                  >
                    {copiadoId === cte.id ? <CheckIcon className="h-4 w-4 text-ok" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                  </button>
                )}
                <button
                  onClick={(e) => handleBaixarXml(e, cte.id)}
                  disabled={baixarXmlMutation.isPending}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx disabled:opacity-50"
                  title="Baixar XML"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => handleGerarDacte(e, cte.id)}
                  disabled={gerarDacteMutation.isPending}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx disabled:opacity-50"
                  title="Gerar DACTE (PDF)"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CteList;
